import React, { useState, useRef, useEffect } from "react";
import "./BookSessionModal.css";
import { State, City } from "country-state-city";

/* ICON PNGs */
import UserIcon from "../../../Assets/Forms/profile.png";
import CompanyIcon from "../../../Assets/Forms/merchant.png";
import PhoneIcon from "../../../Assets/Forms/phone.png";
import DateIcon from "../../../Assets/Forms/calender.png";
import TimeIcon from "../../../Assets/Forms/tym.png";
import SuccessImg from "../../../Assets/Forms/success-hero.jpg";

import { FiRefreshCw } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import CustomDropdown from "../../Common/CustomDropdown";

/* CAPTCHA GENERATOR */
const generateCaptcha = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#";
  let captcha = "";
  for (let i = 0; i < 5; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return captcha;
};

const STORAGE_KEY = "fulupo_session_form_data";

export default function BookSessionModal({ onClose }) {
  const [formData, setFormData] = useState(() => {
    const defaultState = {
      name: "",
      company: "",
      email: "",
      phone: "",
      district: "",
      state: "",
      date: "",
      time: "",
      requirements: ""
    };
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...defaultState, ...parsed };
      } catch (e) {
        console.error("Error parsing saved form data", e);
      }
    }
    return defaultState;
  });

  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [DistrictList, setDistrictList] = useState([]);
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const nameRef = useRef(null);
  const companyRef = useRef(null);
  const phoneRef = useRef(null);
  const districtRef = useRef(null);
  const stateRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Initialize cities list when form data is restored from localStorage
  useEffect(() => {
    if (formData.state) {
      const stateObj = State.getStatesOfCountry("IN").find(
        (s) => s.name === formData.state
      );
      if (stateObj) {
        setDistrictList(City.getCitiesOfState("IN", stateObj.isoCode));
      }
    } else {
      setDistrictList([]);
    }
  }, [formData.state]);

  useEffect(() => {
    // Push item to history to intercept back button
    window.history.pushState({ modal: "session" }, "");

    const handlePopState = () => {
      onClose();
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [onClose]);

  useEffect(() => {
    // Prevent background scrolling when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const openPicker = (ref) => {
    if (ref?.current?.showPicker) ref.current.showPicker();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "phone") {
      finalValue = value.replace(/\D/g, "");
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleStateChange = (stateCode) => {
    // CustomDropdown returns the value directly (isoCode in this case)
    const stateObj = State.getStatesOfCountry("IN").find(
      (s) => s.isoCode === stateCode
    );

    setFormData((prev) => ({
      ...prev,
      state: stateObj ? stateObj.name : "",
      district: "",
    }));

    if (stateCode) {
      setDistrictList(City.getCitiesOfState("IN", stateCode));
    } else {
      setDistrictList([]);
    }

    if (errors.state || errors.district) {
      setErrors((prev) => ({ ...prev, state: false, district: false }));
    }
  };

  const handleCityChange = (cityName) => {
    setFormData((prev) => ({ ...prev, district: cityName }));
    if (errors.district) setErrors((prev) => ({ ...prev, district: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      captchaInput: captchaInput.trim(),
    };

    const newErrors = {};

    if (!data.name) newErrors.name = "Full name is required";
    if (!data.company) newErrors.company = "Company name is required";

    if (!data.district) newErrors.district = "District is required";
    if (!data.state) newErrors.state = "State is required";

    if (!data.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(data.phone)) {
      newErrors.phone = "Must be 10 digits";
    }

    if (!data.date) newErrors.date = "Select a date";
    if (!data.time) newErrors.time = "Select a time";

    if (!data.captchaInput) {
      newErrors.captcha = "Captcha required";
    } else if (data.captchaInput !== captcha) {
      newErrors.captcha = "Captcha incorrect";
      setCaptcha(generateCaptcha());
      setCaptchaInput("");
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);

    const formatTime = (time24) => {
      const [hours, minutes] = time24.split(":");
      let period = "AM";
      let hour = parseInt(hours, 10);

      if (hour >= 12) {
        period = "PM";
        if (hour > 12) hour -= 12;
      }
      if (hour === 0) hour = 12;

      return `${hour}:${minutes} ${period}`;
    };

    const payload = {
      fullName: data.name,
      companyName: data.company,
      phoneNumber: data.phone,
      district: data.district,
      state: data.state,
      preferredDate: data.date,
      preferredTime: formatTime(data.time),
      additionalDetails: data.requirements.trim() || "NA",
    };

    try {
      const response = await fetch("https://fulupostore.tsitcloud.com/api/demoSchedule/schedule", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowSuccess(true);
        localStorage.removeItem(STORAGE_KEY);
        setFormData({
          name: "",
          company: "",
          phone: "",
          district: "",
          state: "",
          date: "",
          time: "",
          requirements: ""
        });
      } else {
        const errorMsg = result.message || "Failed to submit. Please try again.";
        console.error("Submission Error:", result);
        alert(errorMsg);

        if (result.message?.toLowerCase().includes("captcha")) {
          setCaptcha(generateCaptcha());
          setCaptchaInput("");
        }
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoHome = () => {
    onClose();
    setTimeout(() => {
      document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  /* ================= SUCCESS SCREEN ================= */
  if (showSuccess) {

    return (
      <div className="book-session-overlay">
        <div className="book-success-modal" onClick={(e) => e.stopPropagation()}>
          <div className="book-success-left">
            <div className="book-success-badge-row">
              <div className="book-success-check-icon book-check-circle animated">
                <svg className="book-check-svg" viewBox="0 0 52 52">
                  <path
                    className="book-check-path"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="5"
                    d="M14.1 27.2l7.1 7.2 16.7-16.8"
                  />
                </svg>
              </div>
              <span className="book-success-text">SUCCESS</span>
            </div>

            <h2 className="book-success-title">
              Demo Request <br /> Received!
            </h2>

            <p className="book-success-desc">
              Thanks for scheduling a demo with Fulupo. Our team will review your request and confirm your meeting details shortly. You should receive a confirmation within 24 hours.
            </p>


            <button className="book-home-btn" onClick={handleGoHome}>
              Go to Home Page
            </button>
          </div>

          <div className="book-success-right">
            <img src={SuccessImg} alt="Success" className="book-success-image" />

            <div className="book-status-floating-card">
              <div className="book-status-icon-circle">◑</div>
              <div className="book-status-text-col">
                <span className="book-status-label">Status</span>
                <span className="book-status-val">Processing</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="book-session-overlay">
      <div className="book-session-container" onClick={(e) => e.stopPropagation()}>
        <button className="book-session-closed-x" onClick={onClose}>
          <IoClose />
        </button>
        <div className="book-session-modal">
          <div className="book-session-header">
            <h3>Book Your Session</h3>
            <span className="book-free-badge">Free consultation</span>
          </div>

          <form className="book-session-form" onSubmit={handleSubmit}>
            <div className="book-grid-2">
              <div className="book-field">
                <label className={errors.name ? "book-error-label" : ""}>
                  Full Name *
                </label>
                <div className={`book-input-wrap ${errors.name ? "book-error-input" : ""}`} onClick={() => nameRef.current?.focus()}>
                  <img src={UserIcon} className="book-input-icon" alt="" />
                  <input
                    ref={nameRef}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name "
                  />
                </div>
              </div>

              <div className="book-field">
                <label className={errors.company ? "book-error-label" : ""}>
                  Company Name *
                </label>
                <div
                  className={`book-input-wrap ${errors.company ? "book-error-input" : ""
                    }`}
                  onClick={() => companyRef.current?.focus()}
                >
                  <img src={CompanyIcon} className="book-input-icon" alt="" />
                  <input
                    ref={companyRef}
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company Name "
                  />
                </div>
              </div>
            </div>

            <div className="book-field">
              <label className={errors.phone ? "book-error-label" : ""}>Phone Number *</label>
              <div
                className={`book-input-wrap ${errors.phone ? "book-error-input" : ""}`}
                onClick={() => phoneRef.current?.focus()}
              >
                <img src={PhoneIcon} className="book-input-icon" alt="" />
                <input
                  ref={phoneRef}
                  name="phone"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number "
                />
              </div>
            </div>

            <div className="book-grid-2">
              <div className="book-field">
                <label className={errors.state ? "book-error-label" : ""}>State *</label>
                <div
                  className={`book-input-wrap-dropdown ${errors.state ? "book-error-input" : ""}`}
                >
                  <CustomDropdown
                    className="dropdown-no-bottom-radius"
                    options={State.getStatesOfCountry("IN").map(s => ({ label: s.name, value: s.isoCode }))}
                    value={State.getStatesOfCountry("IN").find(
                      (s) => s.name === formData.state
                    )?.isoCode || ""}
                    onChange={handleStateChange}
                    placeholder="Select State*"
                    error={errors.state}
                  />
                </div>
              </div>

              <div className="book-field">
                <label className={errors.district ? "book-error-label" : ""}>District *</label>
                <div
                  className={`book-input-wrap-dropdown ${errors.district ? "book-error-input" : ""}`}
                >
                  <CustomDropdown
                    className="dropdown-no-bottom-radius"
                    options={DistrictList.map(c => ({ label: c.name, value: c.name }))}
                    value={formData.district}
                    onChange={handleCityChange}
                    placeholder="Select District*"
                    disabled={!formData.state}
                    error={errors.district}
                  />
                </div>
              </div>
            </div>

            <div className="book-grid-2">
              <div className="book-field">
                <label className={errors.date ? "book-error-label" : ""}>Preferred Date *</label>
                <div className={`book-input-wrap ${errors.date ? "book-error-input" : ""}`} onClick={() => openPicker(dateRef)}>
                  <img src={DateIcon} className="book-input-icon" alt="" />
                  <input
                    ref={dateRef}
                    name="date"
                    type="date"
                    min={today}
                    value={formData.date}
                    onChange={handleChange}
                    placeholder="Preferred Date *"
                    className="book-hidden-picker"
                  />
                </div>
              </div>

              <div className="book-field">
                <label className={errors.time ? "book-error-label" : ""}>Preferred Time *</label>
                <div className={`book-input-wrap ${errors.time ? "book-error-input" : ""}`} onClick={() => openPicker(timeRef)}>
                  <img src={TimeIcon} className="book-input-icon" alt="" />
                  <input
                    ref={timeRef}
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleChange}
                    placeholder="Preferred Time *"
                    className="book-hidden-picker"
                  />
                </div>
              </div>
            </div>


            <div className="book-field">
              <label>Specific Requirements</label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="Specific Requirements"
              />
            </div>

            {/* CAPTCHA */}
            <div className="book-captcha-section">
              <div className="book-captcha-box">{captcha}</div>

              <button
                type="button"
                className="book-captcha-refresh"
                onClick={() => {
                  setCaptcha(generateCaptcha());
                  setCaptchaInput("");
                }}
              >
                <FiRefreshCw />
              </button>

              <input
                className={`book-captcha-input ${errors.captcha ? "book-error-input" : ""
                  }`}
                placeholder="Enter Captcha here"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
              />
            </div>

            <button className="book-submit-btn" disabled={isLoading}>
              {isLoading ? (
                <div className="book-loader-container">

                  <div className="book-spinner"></div>
                </div>
              ) : "Submit Demo Request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
